// Display condition rules:
// https://gmntc.atlassian.net/wiki/spaces/DS/pages/70090836/Model+Display+Condition#%5BhardBreak%5DFrontend%3A
import {
  parsedDisplayConditionType,
  builtDisplayConditionType,
  parsedDisplayConditionRuleType,
  DisplayConditionFieldsConfig,
  builtDisplayConditionBaseType,
} from "../constants";

const stripParser = (value: string, parser: string) =>
  value.startsWith(parser) ? value.slice(parser.length) : value;

export const parseSingleValue = (value: string | undefined, parser = "in:") => {
  if (!value) return null;

  return stripParser(value, parser);
};

export const parseMultipleValue = (
  value: string | string[] | undefined,
  parser = "in:"
) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value) {
    return [];
  }

  const cleaned = stripParser(value, parser);

  return cleaned
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
};

export const parseDisplayConditionRule = (
  displayCondition: builtDisplayConditionBaseType
): parsedDisplayConditionRuleType => {
  const devicePlatformValue =
    displayCondition?.device_platform?.length > 0
      ? parseMultipleValue(displayCondition?.device_platform)
      : displayCondition?.device != null
      ? [...parseSingleValue(displayCondition?.device)]
      : [];
  return {
    user_state: parseSingleValue(displayCondition?.user_state),
    country: parseMultipleValue(displayCondition?.country),
    locale: parseMultipleValue(displayCondition?.locale),
    device_platform: devicePlatformValue,
    domain: parseMultipleValue(displayCondition?.domain, "intersects_keyed:"),
    abtest: parseSingleValue(displayCondition?.abtest),
    currency: parseMultipleValue(displayCondition?.currency),
    vip_status: parseMultipleValue(displayCondition?.vip_status),
    tags: parseMultipleValue(displayCondition?.tags, "intersects:"),
    btag: parseMultipleValue(displayCondition?.btag, "starts_with:"),
    party_id: parseMultipleValue(displayCondition?.party_id),
  };
};

export const parseDisplayCondition = (
  displayCondition: builtDisplayConditionType
) => ({
  allow: parseDisplayConditionRule(displayCondition),
  deny: parseDisplayConditionRule(displayCondition?.deny),
});

export const buildSingleValue = (value: string, parser = "in:") => {
  return `${parser}${value}`;
};

export const buildMultipleValue = (value: string[], parser = "in:") => {
  return `${parser}${value.join(",")}`;
};

export const buildDisplayConditionRule = (
  displayCondition: parsedDisplayConditionRuleType,
  config: DisplayConditionFieldsConfig
): builtDisplayConditionBaseType => {
  const assembledDisplayCondition: builtDisplayConditionBaseType = {};

  const configValues = Object.values(config) as any;
  const singleValueFields = configValues
    .filter((value) => value.type === "single")
    .map((value) => value.fieldName);
  const multipleValueFields = configValues
    .filter((value) => value.type === "multiple")
    .map((value) => value.fieldName);

  for (const [key, value] of Object.entries(displayCondition)) {
    const isSingleValue = singleValueFields.includes(key);
    const hasSingleValue =
      isSingleValue && value && value !== "all" && value !== "none";

    const isMultipleValue = multipleValueFields.includes(key);
    const hasMultipleValue = isMultipleValue && value && value.length > 0;

    if (hasSingleValue) {
      assembledDisplayCondition[key] = buildSingleValue(
        value as string,
        configValues.find((value) => value.fieldName === key)?.parser
      );
      continue;
    }

    if (hasMultipleValue) {
      assembledDisplayCondition[key] = buildMultipleValue(
        value as string[],
        configValues.find((value) => value.fieldName === key)?.parser
      );
    }
  }

  return assembledDisplayCondition;
};
export const buildDisplayCondition = (
  displayCondition: parsedDisplayConditionType,
  config: DisplayConditionFieldsConfig
): builtDisplayConditionType => {
  const denyConditions = buildDisplayConditionRule(
    displayCondition?.deny,
    config
  );

  return {
    ...buildDisplayConditionRule(displayCondition?.allow, config),
    ...(Object.keys(denyConditions).length > 0 && { deny: denyConditions }),
  };
};
